import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { WebsiteAssessment } from '../../hooks/useWebsiteAssessment';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';

interface WebsiteAssessmentPrintProps {
  assessment: WebsiteAssessment;
  websiteUrl: string;
  companyName?: string;
}

export function WebsiteAssessmentPrint({ assessment, websiteUrl, companyName }: WebsiteAssessmentPrintProps) {
  const { data: companyId } = useCurrentCompany();
  
  // Calculate overall score
  const getScore = () => {
    let score = 0;
    let totalChecks = 0;
    
    // Basic checks (5 points)
    if (assessment.url_works) score++;
    totalChecks++;
    if (assessment.loads_successfully) score++;
    totalChecks++;
    if (assessment.has_phone_number) score++;
    totalChecks++;
    if (assessment.has_email) score++;
    totalChecks++;
    if (assessment.has_address) score++;
    totalChecks++;
    
    // SSL check
    if (assessment.has_ssl) score++;
    totalChecks++;
    
    // Mobile friendly
    if (assessment.mobile_friendly) score++;
    totalChecks++;
    
    // Social links
    if (assessment.has_social_links) score++;
    totalChecks++;
    
    // Online booking
    if (assessment.has_online_booking) score++;
    totalChecks++;
    
    // SEO basics
    if (assessment.has_meta_title) score++;
    totalChecks++;
    if (assessment.has_meta_description) score++;
    totalChecks++;
    if (assessment.has_meta_keywords) score++;
    totalChecks++;
    
    return { score, totalChecks };
  };

  const { score, totalChecks } = getScore();
  const percentage = totalChecks > 0 ? Math.round((score / totalChecks) * 100) : 0;
  
  // Generate consultation booking URL with QR code
  // Always use landing page domain (rollops.pro) for public pages, not app subdomain
  // In dev, use localhost if available, otherwise use production domain
  const getBaseUrl = () => {
    if (typeof window === 'undefined') return 'https://rollops.pro';
    const hostname = window.location.hostname;
    // For local dev, use localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `http://${hostname}:${window.location.port || '3000'}`;
    }
    // For production, always use landing page domain (not app subdomain)
    return 'https://rollops.pro';
  };
  const baseUrl = getBaseUrl();
  const consultationUrl = `${baseUrl}/get-info?source=website-assessment&company=${companyId || 'unknown'}`;

  // Action steps based on failures (prioritized)
  const actionSteps: string[] = [];
  
  // Critical issues first
  if (!assessment.url_works) {
    actionSteps.push('🔴 CRITICAL: Fix website URL - ensure your domain is properly configured and accessible. Check DNS settings and hosting status.');
  }
  if (!assessment.loads_successfully) {
    actionSteps.push('🔴 CRITICAL: Resolve loading errors - check server configuration, hosting status, and error logs. Contact your hosting provider if needed.');
  }
  if (!assessment.has_ssl) {
    actionSteps.push('🟡 IMPORTANT: Enable SSL/HTTPS - secure your website and improve trust. Free SSL certificates available via Let\'s Encrypt.');
  }
  
  // Contact information
  if (!assessment.has_phone_number) {
    actionSteps.push('🟡 IMPORTANT: Add phone number prominently on your homepage - make it easy for customers to contact you. Consider adding to header or footer.');
  }
  if (!assessment.has_email) {
    actionSteps.push('🟡 IMPORTANT: Add contact email or contact form - provide a clear way for inquiries. Consider adding a "Contact Us" page.');
  }
  if (!assessment.has_address) {
    actionSteps.push('🟡 IMPORTANT: Add physical address - help customers find your location. Include on contact page and in footer.');
  }
  
  // Mobile and performance
  if (!assessment.mobile_friendly) {
    actionSteps.push('🟡 IMPORTANT: Improve mobile responsiveness - most customers browse on mobile devices. Test your site on mobile and fix layout issues.');
  }
  if (assessment.load_time_ms && assessment.load_time_ms > 3000) {
    actionSteps.push('🟡 IMPORTANT: Optimize page speed - faster sites rank better and convert more. Compress images, enable caching, minimize JavaScript.');
  }
  
  // Business features
  if (!assessment.has_online_booking) {
    actionSteps.push('🟢 RECOMMENDED: Add online booking - make it easy for customers to schedule classes. Consider Calendly, Acuity, or MindBody integration.');
  }
  if (!assessment.has_social_links) {
    actionSteps.push('🟢 RECOMMENDED: Add social media links - connect with customers on platforms they use. Add icons to footer or header linking to your profiles.');
  }
  if (!assessment.has_testimonials) {
    actionSteps.push('🟢 RECOMMENDED: Add testimonials/reviews section - builds trust and social proof. Display customer reviews prominently on homepage.');
  }
  if (!assessment.has_photo_gallery) {
    actionSteps.push('🟢 RECOMMENDED: Add photo gallery - showcase your facility, classes, and community. Include recent photos of your gym and classes.');
  }
  if (!assessment.has_blog) {
    actionSteps.push('🟢 RECOMMENDED: Add blog/news section - improves SEO and engages customers. Share updates, tips, and gym news regularly.');
  }
  
  // SEO improvements
  if (!assessment.has_meta_title) {
    actionSteps.push('🟢 RECOMMENDED: Add meta title tag - critical for SEO and browser tabs. Keep it under 60 characters and include your gym name.');
  }
  if (!assessment.has_meta_description) {
    actionSteps.push('🟢 RECOMMENDED: Add meta description - appears in search results and improves click-through. Keep it 150-160 characters.');
  }
  
  // Performance scores
  if (assessment.performance_score !== null && assessment.performance_score < 50) {
    actionSteps.push('🟡 PERFORMANCE: Page speed is slow - optimize images, enable browser caching, minimize JavaScript, and consider a CDN.');
  }
  if (assessment.seo_score !== null && assessment.seo_score < 70) {
    actionSteps.push('🟡 SEO: Improve SEO score - add proper meta tags, implement structured data, ensure mobile-friendly design, and improve content structure.');
  }

  return (
    <div className="hidden print:block print:p-8 print:bg-white print:text-black" id="website-assessment-print">
      <style>{`
        @media print {
          @page {
            margin: 1in;
          }
          body * {
            visibility: hidden;
          }
          #website-assessment-print, #website-assessment-print * {
            visibility: visible;
          }
          #website-assessment-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
            color: black;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
        @media screen {
          #website-assessment-print {
            display: none;
          }
        }
      `}</style>
      
      <div className="max-w-4xl mx-auto p-8 bg-white text-black">
        {/* Header with RollOps Branding */}
        <div className="mb-8 border-b-2 border-gray-300 pb-6">
          <div className="flex items-center gap-4 mb-4">
            <img 
              src="/rollops-logo.png" 
              alt="RollOps Logo" 
              width={48} 
              height={48} 
              className="w-12 h-12" 
            />
            <div>
              <div className="text-2xl font-bold text-black">
                Roll<span className="text-rollops-accent">O</span>ps
              </div>
              <div className="text-sm text-gray-500">Free Website Assessment</div>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-2">Website Assessment Report</h1>
          {companyName && (
            <p className="text-xl text-gray-600">{companyName}</p>
          )}
          <p className="text-gray-500 mt-2">{websiteUrl}</p>
          <p className="text-sm text-gray-400 mt-1">
            Assessment Date: {new Date(assessment.assessment_date).toLocaleDateString()}
          </p>
        </div>

        {/* Overall Score */}
        <div className="mb-8 p-6 bg-gray-100 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Overall Score</h2>
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold">{percentage}%</div>
                <div className="text-lg">
                  {score} out of {totalChecks} checks passed
                </div>
              </div>
            </div>
            <div className={`text-4xl ${percentage >= 80 ? 'text-green-600' : percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
              {percentage >= 80 ? '🟢' : percentage >= 60 ? '🟡' : '🔴'}
            </div>
          </div>
        </div>

        {/* Performance Scores */}
        {(assessment.performance_score !== null || assessment.seo_score !== null) && (
          <div className="mb-8 grid grid-cols-2 gap-4">
            {assessment.performance_score !== null && (
              <div className="p-4 border border-gray-300 rounded">
                <h3 className="font-semibold mb-2">Performance Score</h3>
                <div className="text-3xl font-bold">{assessment.performance_score}/100</div>
              </div>
            )}
            {assessment.seo_score !== null && (
              <div className="p-4 border border-gray-300 rounded">
                <h3 className="font-semibold mb-2">SEO Score</h3>
                <div className="text-3xl font-bold">{assessment.seo_score}/100</div>
              </div>
            )}
            {assessment.accessibility_score !== null && (
              <div className="p-4 border border-gray-300 rounded">
                <h3 className="font-semibold mb-2">Accessibility Score</h3>
                <div className="text-3xl font-bold">{assessment.accessibility_score}/100</div>
              </div>
            )}
            {assessment.best_practices_score !== null && (
              <div className="p-4 border border-gray-300 rounded">
                <h3 className="font-semibold mb-2">Best Practices Score</h3>
                <div className="text-3xl font-bold">{assessment.best_practices_score}/100</div>
              </div>
            )}
          </div>
        )}

            {/* Checklist */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Checklist Results</h2>
              
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Basic Functionality</h3>
                <div className="space-y-2">
                  {[
                    { label: 'URL Works', value: assessment.url_works },
                    { label: 'Loads Successfully', value: assessment.loads_successfully },
                    { label: 'Has Phone Number', value: assessment.has_phone_number },
                    { label: 'Has Email', value: assessment.has_email },
                    { label: 'Has Address', value: assessment.has_address },
                  ].map((check) => (
                    <div key={check.label} className="flex items-center gap-3 p-2 border-b border-gray-200">
                      <span className="text-2xl">{check.value ? '✅' : '❌'}</span>
                      <span className="flex-1">{check.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Security & Performance</h3>
                <div className="space-y-2">
                  {[
                    { label: 'SSL/HTTPS Enabled', value: assessment.has_ssl },
                    { label: 'Mobile Friendly', value: assessment.mobile_friendly },
                  ].map((check) => (
                    <div key={check.label} className="flex items-center gap-3 p-2 border-b border-gray-200">
                      <span className="text-2xl">{check.value ? '✅' : '❌'}</span>
                      <span className="flex-1">{check.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Business Features</h3>
                <div className="space-y-2">
                  {[
                    { label: 'Social Media Links', value: assessment.has_social_links },
                    { label: 'Online Booking Available', value: assessment.has_online_booking },
                    { label: 'Testimonials/Reviews', value: assessment.has_testimonials },
                    { label: 'Photo Gallery', value: assessment.has_photo_gallery },
                    { label: 'Blog/News Section', value: assessment.has_blog },
                  ].map((check) => (
                    <div key={check.label} className="flex items-center gap-3 p-2 border-b border-gray-200">
                      <span className="text-2xl">{check.value ? '✅' : '❌'}</span>
                      <span className="flex-1">{check.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">SEO Basics</h3>
                <div className="space-y-2">
                  {[
                    { label: 'Has Meta Title', value: assessment.has_meta_title },
                    { label: 'Has Meta Description', value: assessment.has_meta_description },
                    { label: 'Has Meta Keywords', value: assessment.has_meta_keywords },
                  ].map((check) => (
                    <div key={check.label} className="flex items-center gap-3 p-2 border-b border-gray-200">
                      <span className="text-2xl">{check.value ? '✅' : '❌'}</span>
                      <span className="flex-1">{check.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

        {/* Load Time */}
        {assessment.load_time_ms !== null && (
          <div className="mb-8 p-4 bg-gray-50 rounded">
            <h3 className="font-semibold mb-2">Page Load Time</h3>
            <p className="text-lg">
              {(assessment.load_time_ms / 1000).toFixed(1)} seconds
              {assessment.load_time_ms > 3000 && (
                <span className="text-red-600 ml-2">⚠️ Slow - should be under 3 seconds</span>
              )}
            </p>
          </div>
        )}

        {/* Action Steps */}
        {actionSteps.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Recommended Action Steps</h2>
            <ol className="list-decimal list-inside space-y-2">
              {actionSteps.map((step, index) => (
                <li key={index} className="p-2 bg-yellow-50 border-l-4 border-yellow-400">
                  {step}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Contact Information Found */}
        {(assessment.phone_numbers_found?.length || assessment.emails_found?.length) && (
          <div className="mb-8 p-4 bg-blue-50 rounded">
            <h3 className="font-semibold mb-2">Contact Information Found</h3>
            {assessment.phone_numbers_found && assessment.phone_numbers_found.length > 0 && (
              <div className="mb-2">
                <strong>Phone Numbers:</strong> {assessment.phone_numbers_found.join(', ')}
              </div>
            )}
            {assessment.emails_found && assessment.emails_found.length > 0 && (
              <div>
                <strong>Emails:</strong> {assessment.emails_found.join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Footer with QR Code */}
        <div className="mt-12 pt-8 border-t-2 border-gray-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-2">Ready to Improve Your Website?</h3>
              <p className="text-gray-600 mb-4">
                Schedule a free consultation with our team to discuss website improvements, 
                hosting options, and digital marketing strategies tailored for your gym.
              </p>
              <p className="text-sm text-gray-500">
                Scan the QR code or visit: {consultationUrl}
              </p>
            </div>
            <div className="ml-8 p-4 bg-white border-2 border-gray-300 rounded">
              <QRCodeSVG value={consultationUrl} size={150} />
            </div>
          </div>
        </div>

        {/* Footer with Branding */}
        <div className="mt-8 pt-4 border-t border-gray-200 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img 
              src="/rollops-logo.png" 
              alt="RollOps Logo" 
              width={24} 
              height={24} 
              className="w-6 h-6" 
            />
            <span className="text-sm font-bold text-gray-700">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
          </div>
          <p className="text-sm text-gray-500">Generated by RollOps Website Assessment Tool</p>
          <p className="text-xs text-gray-400 mt-1">For questions or support, contact your RollOps representative</p>
        </div>
      </div>
    </div>
  );
}
